import { ref, computed } from 'vue';
import type { FormField, ResponseInForm, ValidationRule } from '../../../types/formTypeStructure';
import { shouldDisplayFormField, shouldDisplaySection } from '../../../types/displayRules';
import { getLocalizedText } from '../../../utils/formHelpers';
import { extractErrorMessages } from '../../../utils/validationHelpers';
import { hasOTPField, getOTPField } from '../../../types/formUtils';
import type { FormValidationResult, FieldValidationError } from '../types/formEngine.types';

interface ValidationMethods {
  validate: (fieldNames?: string[]) => Promise<{ valid: boolean }>;
  meta: {
    value: {
      touched?: Record<string, boolean>;
      touchedFields?: Record<string, boolean>;
    };
  };
  errors: {
    value: Record<string, any>;
  };
}

export function useFormPageValidation(
  navigation: any,
  formValues: Record<string, any>,
  validationMethods: ValidationMethods,
  currentLang: string = 'fr',
  paginationMode: 'byFields' | 'bySection' = 'byFields'
) {
  const isValidating = ref(false);
  const { validate, meta: formMeta, errors: formErrors } = validationMethods;

  const validatePage = async (): Promise<FormValidationResult> => {
    isValidating.value = true;

    try {
      // Convertir les valeurs pour les règles d'affichage
      const allResponseInForms: Record<string, ResponseInForm> = {};
      Object.entries(formValues).forEach(([key, value]) => {
        allResponseInForms[key] = { responseValue: value };
      });

      // ÉTAPE 1: Récupérer uniquement les champs de la page actuelle
      const currentPageFields = navigation.pagedFields.value || [];
      const currentSection = navigation.currentSection.value;
      const currentStepInfo = paginationMode === 'bySection'
        ? `Section ${navigation.sectionIndex.value + 1}`
        : `Section ${navigation.sectionIndex.value + 1}, Page ${navigation.fieldPage.value + 1}`;

      // 🆕 Détecter si c'est une section OTP
      const isOTPSection = currentSection ? hasOTPField(currentSection) : false;
      const otpField = isOTPSection && currentSection ? getOTPField(currentSection) : undefined;

      // 🆕 Pour les sections OTP, valider que l'OTP a été rempli
      if (isOTPSection && otpField) {
        const otpValue = formValues[otpField.fieldName];
        const otpLength = otpField.otpFormFieldExecOptions?.otpLength ?? 6;
        
        // Vérifier que l'OTP est rempli (string de la bonne longueur)
        if (!otpValue || typeof otpValue !== "string" || otpValue.length !== otpLength) {
          const fieldLabel = getLocalizedText(otpField.label, currentLang);
          return {
            isValid: false,
            touched: false,
            allRequiredFieldsFilled: false,
            visibleFieldsCount: 0,
            requiredFieldsCount: 0,
            invalidFields: [],
            untouchedRequiredFields: [],
            emptyRequiredFields: [{
              fieldName: otpField.fieldName,
              fieldLabel,
              // Message explicite pour le toast : blocage tant que l'OTP n'est pas correctement rempli
              errors: [`Veuillez remplir le code OTP (${otpLength} chiffres) avant de continuer`]
            }]
          };
        }
        
        // Si l'OTP est rempli, la validation passe (pas besoin de valider les autres champs)
        return {
          isValid: true,
          touched: true,
          allRequiredFieldsFilled: true,
          visibleFieldsCount: 1,
          requiredFieldsCount: 0,
          invalidFields: [],
          untouchedRequiredFields: [],
          emptyRequiredFields: []
        };
      }

      // ÉTAPE 2: Filtrer les champs visibles selon les règles d'affichage
      const visibleCurrentPageFields = currentPageFields.filter((field: FormField) => {
        // Vérifier si la section actuelle est visible
        const sectionVisible = shouldDisplaySection(currentSection, allResponseInForms);
        
        // Vérifier si le champ lui-même doit être affiché
        const shouldDisplay = shouldDisplayFormField(field, allResponseInForms);
        
        const isVisible = sectionVisible && shouldDisplay;
        return isVisible;
      });

      // ÉTAPE 3: Gérer les champs répétés (repeat groups) pour la page actuelle
      const repeatFieldsForCurrentPage = visibleCurrentPageFields
        .filter((f: FormField) => f.repeatGroup)
        .flatMap((f: FormField) => {
          const group = f.repeatGroup!;
          const instanceCount = formValues[group.fieldName]?.length ?? 0;

          const repeatedFields: FormField[] = [];

          for (let i = 0; i < instanceCount; i++) {
            for (const baseField of group.formFields) {
              const repeatedField = {
                ...baseField,
                fieldName: `${group.fieldName}_${baseField.fieldName}_${i}`
              };

              if (shouldDisplayFormField(repeatedField, allResponseInForms)) {
                repeatedFields.push(repeatedField);
              }
            }
          }

          return repeatedFields;
        });

      // ÉTAPE 4: Combiner tous les champs à vérifier pour cette page
      const allFieldsToCheck = [...visibleCurrentPageFields, ...repeatFieldsForCurrentPage];

      // ÉTAPE 5: Validation des champs avec Vee-Validate (comme React utilise trigger)
      // Valider uniquement les champs de la page actuelle
      const fieldNames = allFieldsToCheck.map(f => f.fieldName).filter(Boolean);
      let isValid = true;
      
      if (fieldNames.length > 0) {
        try {
          console.log('🔍 [Validation Zod] Validation des champs:', fieldNames);
          console.log('🔍 [Validation Zod] Valeurs actuelles:', Object.fromEntries(
            fieldNames.map(name => [name, formValues[name]])
          ));
          
          // Valider tous les champs (Vee-Validate valide tout le schéma)
          const validationResult = await validate();
          
          // Filtrer les erreurs pour ne considérer QUE les champs de la page actuelle
          // (comme React ne valide que les champs spécifiés avec trigger)
          const errorsForCurrentPage: Record<string, any> = {};
          Object.entries(formErrors.value).forEach(([fieldName, error]) => {
            if (fieldNames.includes(fieldName)) {
              errorsForCurrentPage[fieldName] = error;
            }
          });
          
          // isValid est true seulement si les champs de la page actuelle n'ont pas d'erreurs
          isValid = Object.keys(errorsForCurrentPage).length === 0;
          
          if (!isValid) {
            console.log('❌ [Validation Zod] Validation échouée pour les champs de la page');
            console.log('❌ [Validation Zod] Erreurs pour la page actuelle:', errorsForCurrentPage);
            // Afficher les erreurs par champ
            Object.entries(errorsForCurrentPage).forEach(([fieldName, error]) => {
              console.log(`❌ [Validation Zod] Erreur pour ${fieldName}:`, error);
            });
          } else {
            console.log('✅ [Validation Zod] Tous les champs de la page sont valides');
          }
        } catch (error) {
          console.error('❌ [Validation Zod] Erreur lors de la validation:', error);
          isValid = false;
        }
      } else {
        console.log('⚠️ [Validation Zod] Aucun champ à valider');
      }

      // Créer une map des champs pour retrouver rapidement les infos
      const fieldsMap = new Map(allFieldsToCheck.map(f => [f.fieldName, f]));

      // ÉTAPE 6: Identifier les champs requis
      const requiredFields = allFieldsToCheck.filter((field) => {
        const isRequired = field.validations?.some(
          (v: ValidationRule) => v.validationType === 'required'
        );
        if (isRequired) {
          console.log(`📋 [Required] Champ requis: ${field.fieldName}`);
        }
        return isRequired;
      });
      
      console.log(`📋 [Required] Total champs requis: ${requiredFields.length}`, requiredFields.map(f => f.fieldName));
      console.log(`📊 [FormValues] Toutes les valeurs du formulaire:`, JSON.stringify(formValues, null, 2));
      console.log(`📊 [FormValues] Valeurs pour les champs requis:`, Object.fromEntries(
        requiredFields.map(f => [f.fieldName, formValues[f.fieldName]])
      ));

      // ÉTAPE 7: Vérifier que tous les champs requis ont été touchés
      const allRequiredFieldsTouched = requiredFields.every((field) => {
        // Vee-Validate utilise meta.value.touched, pas touchedFields
        const isTouched = !!formMeta.value.touched?.[field.fieldName] || !!formMeta.value.touchedFields?.[field.fieldName];
        return isTouched;
      });

      // ÉTAPE 8: Vérifier que tous les champs requis sont remplis (EXACTEMENT comme React ligne 150-163)
      const allRequiredFieldsFilled = requiredFields.every((field) => {
        const fieldValue = formValues[field.fieldName];
        const filled = Array.isArray(fieldValue)
          ? fieldValue.length > 0
          : fieldValue !== undefined && fieldValue !== null && fieldValue !== '';

        // Log pour déboguer
        console.log(`🔍 [Filled Check] Champ: ${field.fieldName}, Valeur:`, fieldValue, `Type:`, typeof fieldValue, `Filled:`, filled);

        if (!filled) {
          console.warn(`⚠️ [Non rempli] Champ requis vide: ${field.fieldName} →`, fieldValue);
        }

        return filled;
      });
      
      console.log(`✅ [All Required Filled] Tous les champs requis sont remplis: ${allRequiredFieldsFilled}`);

      // ÉTAPE 9: Vérifier si tous les champs (requis ou non) ont été touchés
      const allFieldsTouched = allFieldsToCheck.every((field) => {
        // Vee-Validate utilise meta.value.touched, pas touchedFields
        return !!formMeta.value.touched?.[field.fieldName] || !!formMeta.value.touchedFields?.[field.fieldName];
      });

      // ÉTAPE 10: Extraire les erreurs détaillées pour les toasts
      // 10.1: Erreurs de validation Zod (UNIQUEMENT pour les champs de la page actuelle)
      const invalidFields: FieldValidationError[] = [];
      const errors = formErrors.value;
      if (errors) {
        Object.entries(errors).forEach(([fieldName, error]) => {
          // Filtrer pour ne considérer QUE les champs de la page actuelle
          if (fieldNames.includes(fieldName)) {
            const field = fieldsMap.get(fieldName);
            if (field && error) {
              const messages = extractErrorMessages(error);
              if (messages.length > 0) {
                invalidFields.push({
                  fieldName,
                  fieldLabel: getLocalizedText(field.label, currentLang),
                  errors: messages
                });
              }
            }
          }
        });
      }

      // 10.2: Champs requis non touchés
      const untouchedRequiredFields: FieldValidationError[] = [];
      requiredFields.forEach(field => {
        // Vee-Validate utilise meta.value.touched, pas touchedFields
        const isTouched = !!formMeta.value.touched?.[field.fieldName] || !!formMeta.value.touchedFields?.[field.fieldName];
        if (!isTouched) {
          untouchedRequiredFields.push({
            fieldName: field.fieldName,
            fieldLabel: getLocalizedText(field.label, currentLang),
            errors: ['Champ requis non rempli']
          });
        }
      });

      // 10.3: Champs requis vides
      const emptyRequiredFields: FieldValidationError[] = [];
      requiredFields.forEach(field => {
        const fieldValue = formValues[field.fieldName];
        const isEmpty = Array.isArray(fieldValue)
          ? fieldValue.length === 0
          : fieldValue === undefined || fieldValue === null || fieldValue === '';
        
        // Log pour déboguer
        console.log(`🔍 [Empty Check] Champ: ${field.fieldName}, Valeur:`, fieldValue, `Type:`, typeof fieldValue, `IsEmpty:`, isEmpty);

        if (isEmpty) {
          console.warn(`⚠️ [Empty Required] Champ requis vide détecté: ${field.fieldName} →`, fieldValue);
          emptyRequiredFields.push({
            fieldName: field.fieldName,
            fieldLabel: getLocalizedText(field.label, currentLang),
            errors: ['Ce champ est obligatoire']
          });
        }
      });
      
      console.log(`📊 [Empty Required] Total champs requis vides: ${emptyRequiredFields.length}`, emptyRequiredFields.map(f => f.fieldName));

      // Résultat final - EXACTEMENT comme dans React (ligne 219)
      // isValid = isValid (Zod) && allRequiredFieldsFilled
      const finalIsValid = isValid && allRequiredFieldsFilled;

      const result: FormValidationResult = {
        isValid: finalIsValid,
        touched: allFieldsTouched,
        allRequiredFieldsFilled,
        visibleFieldsCount: allFieldsToCheck.length,
        requiredFieldsCount: requiredFields.length,
        invalidFields,
        untouchedRequiredFields,
        emptyRequiredFields
      };

      // Log pour le débogage
      console.log('🔍 [Validation] Résultat:', {
        isValid: finalIsValid,
        zodValid: isValid,
        allRequiredFieldsFilled,
        invalidFieldsCount: invalidFields.length,
        emptyRequiredFieldsCount: emptyRequiredFields.length,
        untouchedRequiredFieldsCount: untouchedRequiredFields.length,
        requiredFieldsCount: requiredFields.length,
        visibleFieldsCount: allFieldsToCheck.length,
        fieldNames: allFieldsToCheck.map(f => f.fieldName)
      });

      return result;
    } catch (error) {
      console.error('❌ Erreur lors de la validation:', error);
      return {
        isValid: false,
        touched: false,
        allRequiredFieldsFilled: false,
        visibleFieldsCount: 0,
        requiredFieldsCount: 0,
        invalidFields: [],
        untouchedRequiredFields: [],
        emptyRequiredFields: []
      };
    } finally {
      isValidating.value = false;
    }
  };

  return {
    validatePage,
    isValidating: computed(() => isValidating.value),
    setIsValidating: (value: boolean) => { isValidating.value = value; }
  };
}

